import {
  AuthenticationServiceProvider,
  ProductType,
} from 'module/api/account/types';
import { TokenError } from 'module/authentication/components/token-error';
import { TokenErrorStatus } from 'module/authentication/types';
import { getTokenMeta } from 'module/authentication/utils/decode-token';
import { useQueryParams } from 'module/helpers/location-query-params';
import SmallJumpBigLeap from 'module/ui/assets/small_jump_big_leap.png';
import RocketlaneLogoSvg from 'module/ui/assets/svgs/rocketlane_logo_full.svg';
import { ProductiveHeading03 } from 'module/ui/components/styles';
import { Col, Image, Row, Space } from 'antd';
import { AccountAPI } from 'api/account';
import { ROCKETLANE_EMAILS } from 'constant';
import { useAccountMetaQuery } from 'queries/account';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountSetupForm } from './form';
import { MultipleAccounts } from './multiple-accounts';

const StyledRow = styled(Row)`
  background-color: var(--gray-10);
  height: 100vh;
`;

const Left = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const LeftTop = styled.div`
  padding: var(--spacing-08) var(--spacing-10);
`;

const LeftBottom = styled.div`
  height: 50%;
  background-image: url(${SmallJumpBigLeap});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const Right = styled(Col)`
  background-color: var(--white-100);
`;

const FlexBox = styled.div`
  display: flex;
  justify-content: center;
  padding-top: var(--spacing-10);
`;

const Container = styled.div`
  width: 368px;
`;

const BaseHeading = styled.div`
  ${ProductiveHeading03};
  width: 80%;
`;

const PrimaryHeading = styled(BaseHeading)`
  font-weight: var(--font-weight-bold);
`;

const SecondaryHeading = BaseHeading;

enum NextPageFlag {
  SingleAccount = 'single',
  MultipleAccount = 'multiple',
}
interface Props {
  productType: ProductType;
}

export const AccountActivatePage: FC<React.PropsWithChildren<Props>> = ({
  productType,
}) => {
  const params = useQueryParams();
  const token = params.get('token') ?? '';
  const credential = params.get('credential') ?? '';
  const domainCheckToken = params.get('signup_token') ?? '';
  const { decoded, isExpired } = getTokenMeta(token || credential || '');
  const nextPageFlag = params.get('next_page') ?? '';
  const domain = params.get('domain') ?? '';
  const loginToken = params.get('login_token') ?? '';
  const { data } = useAccountMetaQuery();
  const isCognitoEnabled =
    data?.okta.authenticationServiceProvider ===
    AuthenticationServiceProvider.COGNITO;

  if (!decoded) {
    return (
      <TokenError
        status={TokenErrorStatus.Invalid}
        supportEmail={ROCKETLANE_EMAILS.support}
        loginUrl={'/login'}
      />
    );
  }
  if (nextPageFlag && nextPageFlag === NextPageFlag.SingleAccount && domain) {
    window.location.replace(
      `https://${domain}?token=${loginToken}&overrideAuth=true`,
    );

    return <></>;
  }

  if (nextPageFlag && nextPageFlag === NextPageFlag.MultipleAccount) {
    return <MultipleAccounts email={decoded.email ? decoded.email : ''} />;
  }

  if (isExpired) {
    return (
      <TokenError
        email={decoded.emailId}
        status={TokenErrorStatus.Expired}
        resendEmail={() => AccountAPI.resendAccountActivation()}
        supportEmail={ROCKETLANE_EMAILS.support}
        loginUrl={'/login'}
      />
    );
  }

  return (
    <>
      <StyledRow gutter={0} wrap={false}>
        <Left flex="auto">
          <LeftTop>
            <Space direction="vertical" size={84}>
              <div>
                <Image
                  width={160}
                  src={RocketlaneLogoSvg}
                  alt="Rocketlane"
                  preview={false}
                />
              </div>
              <Space direction="vertical" size={16}>
                <PrimaryHeading>
                  <FormattedMessage id="account_setup.title" />
                </PrimaryHeading>
                <SecondaryHeading>
                  <FormattedMessage id="account_setup.message" />
                </SecondaryHeading>
              </Space>
            </Space>
          </LeftTop>
          <LeftBottom />
        </Left>
        <Right flex="600px">
          <FlexBox>
            <Container>
              <Space direction="vertical" size={24}>
                <div>
                  <FormattedMessage
                    id="account_setup.signing_up_as"
                    values={{
                      b: (chunks) => <b>{chunks}</b>,
                      emailId: credential
                        ? decoded.email
                          ? decoded.email
                          : ''
                        : decoded.emailId,
                    }}
                  />
                </div>
                <AccountSetupForm
                  isCognitoEnabled={isCognitoEnabled}
                  token={token || credential}
                  emailId={
                    credential
                      ? decoded.email
                        ? decoded.email
                        : ''
                      : decoded.emailId
                  }
                  throughGoogleAuth={!!credential}
                  name={credential && decoded.name}
                  domainCheckToken={credential && domainCheckToken}
                  productType={productType}
                />
              </Space>
            </Container>
          </FlexBox>
        </Right>
      </StyledRow>
    </>
  );
};
